import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, map, catchError, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { SelectPesquisavelService } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.service';
import {
  PesquisaCodigoTributacaoModalComponent,
  PesquisaCodigoTributacaoModalData,
  PesquisaCodigoTributacaoModalResult,
} from '../../../../../shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { LookupItemStruct } from '../../../../../shared/services/structs/pessoa/lookup-item.struct';
import { UnidadeMedidaStruct } from '../../../../../shared/services/structs/produto/unidade-medida.struct';
import { MaskService, Masks } from '../../../../../shared/services/mask.service';
import { Embalagem } from '../../../../../shared/services/models/produto/embalagem.model';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-produto-dados-gerais',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatDialogModule,
    NgxMaskDirective,
    NgxCurrencyDirective,
    SelectPesquisavelComponent,
  ],
  templateUrl: './produto-dados-gerais.component.html',
  styleUrl: './produto-dados-gerais.component.css',
})
export class ProdutoDadosGeraisComponent implements OnChanges, OnInit, OnDestroy {
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) isEditMode!: boolean;
  @Input() idProdutoPai: number | null = null;
  @Input() masks!: Masks;
  @Input() percentageMask!: any;
  @Input() isBuscandoGtin = false;
  @Output() buscarGtin = new EventEmitter<void>();
  @Input() integerMask!: any;

  private destroy$ = new Subject<void>();
  private readonly cestLookupUrl = environment.urlApiMotorFiscal + 'Lookup/cest';

  constructor(
    private dialog: MatDialog,
    private selectPesquisavelService: SelectPesquisavelService,
  ) { }

  ngOnInit(): void {
    this.idEmbalagemControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(id => this.handleEmbalagemChange(id));

    if (this.idEmbalagemControl.value) {
      this.handleEmbalagemChange(this.idEmbalagemControl.value);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isEditMode'] || changes['form']) {
      this.handleEditMode();
    }
    if (changes['idProdutoPai'] || changes['form']) {
      this.handleProdutoPai();
    }
    if (changes['listaEmbalagem'] && this.listaEmbalagem && this.listaEmbalagem.length > 0) {
      if (this.idEmbalagemControl.value) {
        this.handleEmbalagemChange(this.idEmbalagemControl.value);
      }
    }
  }

  private handleEmbalagemChange(idEmbalagem: number | null | undefined): void {
    const controls = [
      this.form.get('alturaCm'),
      this.form.get('larguraCm'),
      this.form.get('comprimentoCm'),
      this.form.get('diametroCm'),
    ];

    if (idEmbalagem && this.listaEmbalagem && this.listaEmbalagem.length > 0) {
      const embalagem = this.listaEmbalagem.find(e => e.idEmbalagem === idEmbalagem);
      if (embalagem) {
        this.form.patchValue(
          {
            alturaCm: embalagem.alturaCm || 0,
            larguraCm: embalagem.larguraCm || 0,
            comprimentoCm: embalagem.comprimentoCm || 0,
            diametroCm: embalagem.diametroCm || 0,
          },
          { emitEvent: false }
        );
        controls.forEach(c => c?.disable({ emitEvent: false }));
        return;
      }
    }

    controls.forEach(c => c?.enable({ emitEvent: false }));
  }

  private handleEditMode(): void {
    if (this.isEditMode) {
      this.form.get('idTipoProduto')?.disable();
    } else {
      this.form.get('idTipoProduto')?.enable();
    }
  }

  private handleProdutoPai(): void {
    if (this.idProdutoPai && this.isEditMode) {
      this.form.get('nome')?.disable();
      this.form.get('idMarca')?.disable();
      this.form.get('idCategoria')?.disable();
    } else {
      // Re-enable only if not in a global disabled state (which we don't strictly have here, but good practice)
      // For now, simple re-enable is fine as per requirements (Standard Product -> Enabled)
      this.form.get('nome')?.enable();
      this.form.get('idMarca')?.enable();
      this.form.get('idCategoria')?.enable();
    }
  }

  // Lookups
  @Input() listaTipoProduto: LookupItemStruct[] = [];
  @Input() listaTipoItemSped: LookupItemStruct[] = [];
  @Input() listaUnidadeMedida: UnidadeMedidaStruct[] = [];
  @Input() listaOrigem: LookupItemStruct[] = [];
  @Input() listaEmbalagem: any[] | undefined;
  @Input() urlNcm: string = '';
  @Output() tipoProdutoChange = new EventEmitter<number>();

  // Getters for FormControls (para facilitar binding no template)
  get idTipoProdutoControl() {
    return this.form.get('idTipoProduto') as FormControl;
  }
  get idTipoItemSpedControl() {
    return this.form.get('idTipoItemSped') as FormControl;
  }
  get idUnidadeMedidaControl() {
    return this.form.get('idUnidadeMedida') as FormControl;
  }
  get idOrigemMercadoriaControl() {
    return this.form.get('idOrigemMercadoria') as FormControl;
  }
  get idEmbalagemControl(): FormControl {
    return this.form.get('idEmbalagem') as FormControl;
  }
  get ncmControl(): FormControl {
    return this.form.get('ncm') as FormControl;
  }
  get cestControl(): FormControl {
    return this.form.get('cest') as FormControl;
  }

  onTipoProdutoChange(tipo: any): void {
    this.tipoProdutoChange.emit(tipo.id);
  }

  onBuscarGtin(): void {
    this.buscarGtin.emit();
  }

  /** Bloqueia caracteres não numéricos. O tamanho e o ponto são controlados pelo ngx-mask. */
  public onCodigoKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    const isShortcut = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (allowed.includes(event.key) || isShortcut) return;
    if (!/^\d$/.test(event.key)) { event.preventDefault(); }
  }

  /** Abre o modal de pesquisa de NCM. A urlNcm já vem filtrada pelo nível subitem (tipoNcm). */
  public pesquisarNcm(): void {
    const url = this.urlNcm;
    const dialogRef = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
      width: '700px',
      maxHeight: '85vh',
      panelClass: 'pesquisa-codigo-tributacao-dialog',
      data: {
        titulo: 'Pesquisar NCM',
        buscarFn: (pesquisa: string) =>
          this.selectPesquisavelService.getSelectValues<any>(url, pesquisa).pipe(
            map(list => ({
              isError: false,
              itens: list.map((i: any) => ({ codigo: String(i.codigo ?? ''), descricao: i.descricao })),
            })),
            catchError(() => of({ isError: true, itens: [] })),
          ),
        permitirManual: false,
        registrosIniciais: 20,
        valorInicial: this.ncmControl?.value ?? undefined,
      } as PesquisaCodigoTributacaoModalData,
    });
    dialogRef.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
      if (!result?.selecionado) return;
      this.ncmControl?.setValue(result.selecionado.codigo.replace(/\D/g, ''));
      this.ncmControl?.markAsDirty();
    });
  }

  /** Abre o modal de pesquisa de CEST (somente códigos completos). */
  public pesquisarCest(): void {
    const dialogRef = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
      width: '700px',
      maxHeight: '85vh',
      panelClass: 'pesquisa-codigo-tributacao-dialog',
      data: {
        titulo: 'Pesquisar CEST',
        buscarFn: (pesquisa: string) =>
          this.selectPesquisavelService.getSelectValues<any>(this.cestLookupUrl, pesquisa).pipe(
            map(list => ({
              isError: false,
              itens: list.map((i: any) => ({
                codigo: String(i.codigo ?? ''),
                descricao: String(i.descricao ?? '').replace(/<[^>]*>/g, ''),
              })),
            })),
            catchError(() => of({ isError: true, itens: [] })),
          ),
        permitirManual: false,
        registrosIniciais: 20,
        valorInicial: this.cestControl?.value ?? undefined,
      } as PesquisaCodigoTributacaoModalData,
    });
    dialogRef.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
      if (!result?.selecionado) return;
      this.cestControl?.setValue(result.selecionado.codigo.replace(/\D/g, ''));
      this.cestControl?.markAsDirty();
    });
  }
}
